import { useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IRole } from '~/types/role'

import { addAuthsInRole, createRoles, deleteAuthsInRole, getRole, getRoles, updateRole } from '~/api/roles'

////////// KEYS //////////

export const keys = {
  all: ['roles'] as const,
  details: (roleId?: string) => [...keys.all, 'details', roleId] as const,
  list: () => [...keys.all, 'list'] as const,
}

////////// QUERIES //////////

export const useRolesListQuery = (): UseQueryResult<{
  data: IRole[]
  total: number
}> => {
  return useQuery({ queryFn: getRoles, queryKey: keys.list() })
}

export const useRoleDetailsQuery = (roleId?: string): UseQueryResult<IRole> => {
  return useQuery({
    enabled: !!roleId,
    queryFn: () => getRole(roleId as string),
    queryKey: keys.details(roleId),
  })
}

////////// MUTATIONS //////////

export const useCreateRolesMutation = (): UseMutationResult<
  IRole[],
  unknown,
  { authorization_keys: string[]; description: string; name: string }[]
> => {
  return useMutation({ mutationFn: createRoles })
}

export const useUpdateRoleMutation = (): UseMutationResult<
  IRole,
  unknown,
  { description?: string | undefined; id: string; name: string }
> => {
  return useMutation({ mutationFn: updateRole })
}

export const useAddAuthsToRoleMutation = (): UseMutationResult<
  IRole,
  unknown,
  { authKeys: string[]; roleId: string }
> => {
  return useMutation({ mutationFn: addAuthsInRole })
}

export const useDeleteAuthsInRoleMutation = (): UseMutationResult<
  IRole,
  unknown,
  { authKeys: string[]; roleId: string }
> => {
  return useMutation({ mutationFn: deleteAuthsInRole })
}
